import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { DollarSign, TrendingUp, TrendingDown, Users, Gamepad2, ShieldCheck, RefreshCw, Download, AlertTriangle } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

function Stat({ label, value, sub, tone = "default", testid }) {
  const toneCls = {
    default: "text-slate-100",
    gold: "text-[color:var(--gold)]",
    green: "text-emerald-400",
    red: "text-red-400",
  }[tone];
  return (
    <div className="vexis-card p-5" data-testid={testid}>
      <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 font-display text-3xl ${toneCls}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

const RANGES = [
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
];

export default function AdminRevenue() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [ts, setTs] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [range, setRange] = useState(30);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [r, s, f] = await Promise.all([
        api.get("/admin/revenue"),
        api.get(`/admin/revenue/timeseries?days=${range}`),
        api.get("/admin/fraud-alerts"),
      ]);
      setData(r.data);
      setTs(s.data.series);
      setAlerts(f.data.alerts);
    } finally { setBusy(false); }
  };
  useEffect(() => { if (user?.role === "admin") load(); /* eslint-disable-next-line */ }, [user, range]);

  const download = async (kind) => {
    try {
      const token = localStorage.getItem("vexis_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/export/${kind}.csv?days=${range}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `vexis_${kind}_${range}d.csv`;
      a.click();
    } catch { /* ignore */ }
  };

  if (user === null) return null;
  if (!user || user.role !== "admin") return <Navigate to="/lobby" replace />;
  if (!data) return <div className="text-slate-400" data-testid="revenue-loading">Loading revenue…</div>;

  const usd = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="pill pill-gold inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Admin · Revenue</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-2">Business overview</h1>
          <p className="text-sm text-slate-400 mt-2">
            House edge <span className="text-[color:var(--gold)] font-mono">{data.house_edge_pct}%</span> · RTP {(data.rtp * 100).toFixed(0)}%.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => download("deposits")} className="btn-ghost text-sm inline-flex items-center gap-2" data-testid="export-deposits"><Download className="w-4 h-4" /> Deposits CSV</button>
          <button onClick={() => download("withdrawals")} className="btn-ghost text-sm inline-flex items-center gap-2" data-testid="export-withdrawals"><Download className="w-4 h-4" /> Cashouts CSV</button>
          <button onClick={load} disabled={busy} className="btn-ghost text-sm inline-flex items-center gap-2" data-testid="revenue-refresh"><RefreshCw className="w-4 h-4" /> Refresh</button>
        </div>
      </div>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat testid="stat-net-revenue" tone={data.net_revenue_usd >= 0 ? "gold" : "red"}
          label="Net revenue" value={usd(data.net_revenue_usd)} sub="Deposits − payouts" />
        <Stat testid="stat-deposits" tone="green"
          label="Deposits" value={usd(data.deposits.usd)}
          sub={`${data.deposits.count} purchase${data.deposits.count === 1 ? "" : "s"}`} />
        <Stat testid="stat-payouts" tone="red"
          label="Cashouts paid" value={usd(data.payouts.usd)}
          sub={`${data.payouts.count} withdrawal${data.payouts.count === 1 ? "" : "s"}`} />
        <Stat testid="stat-users"
          label="Users" value={data.users.total} sub={`${data.users.paying} paying`} />
      </section>

      {/* Timeseries */}
      <section className="vexis-card p-5">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="pill inline-flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Timeseries</div>
            <h2 className="font-display text-2xl mt-2">Net revenue &amp; hold %</h2>
          </div>
          <div className="flex gap-1">
            {RANGES.map((r) => (
              <button key={r.days} onClick={() => setRange(r.days)} data-testid={`range-${r.days}`}
                className={`px-3 py-1.5 rounded-full text-xs border ${range === r.days ? "border-[color:var(--gold)] text-[color:var(--gold)]" : "border-white/10 text-slate-400"}`}>{r.label}</button>
            ))}
          </div>
        </div>
        <div className="mt-4 h-72" data-testid="revenue-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={ts || []} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 6" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" stroke="#64748B" fontSize={11} tickFormatter={(d) => d.slice(5)} />
              <YAxis yAxisId="usd" stroke="#E2B96A" fontSize={11} tickFormatter={(v) => `$${v}`} />
              <YAxis yAxisId="pct" orientation="right" stroke="#10B981" fontSize={11} tickFormatter={(v) => `${v}%`} />
              <Tooltip contentStyle={{ background: "#0E1218", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }}
                labelStyle={{ color: "#94A3B8" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="usd" type="monotone" dataKey="net_revenue_usd" name="Net revenue $" stroke="#E2B96A" strokeWidth={2} dot={false} />
              <Line yAxisId="pct" type="monotone" dataKey="hold_pct" name="Hold %" stroke="#10B981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="vexis-card p-5">
        <div className="flex items-end justify-between">
          <div>
            <div className="pill inline-flex items-center gap-1"><Gamepad2 className="w-3.5 h-3.5" /> Gameplay hold</div>
            <h2 className="font-display text-2xl mt-2">Chip flow by game</h2>
          </div>
          <div className="text-xs text-slate-400 font-mono">
            wagered {data.gameplay.chips_wagered.toLocaleString()} · paid {data.gameplay.chips_paid_out.toLocaleString()} · net hold <span className="text-[color:var(--gold)]">{data.gameplay.chips_held.toLocaleString()}</span>
          </div>
        </div>
        <div className="mt-4 overflow-hidden rounded-xl border border-white/5">
          <div className="grid grid-cols-12 px-4 py-2 border-b border-white/5 text-[11px] font-mono uppercase tracking-widest text-slate-500">
            <div className="col-span-3">Game</div>
            <div className="col-span-2 text-right">Sessions</div>
            <div className="col-span-2 text-right">Wagered</div>
            <div className="col-span-2 text-right">Paid</div>
            <div className="col-span-2 text-right">Net hold</div>
            <div className="col-span-1 text-right">Hold %</div>
          </div>
          <ul className="divide-y divide-white/5">
            {data.gameplay.per_game.map((g) => (
              <li key={g.game} className="grid grid-cols-12 px-4 py-2 items-center text-sm" data-testid={`revenue-game-${g.game}`}>
                <div className="col-span-3 capitalize text-slate-200">{g.game}</div>
                <div className="col-span-2 text-right font-mono text-slate-400">{g.sessions}</div>
                <div className="col-span-2 text-right font-mono">{g.wagered.toLocaleString()}</div>
                <div className="col-span-2 text-right font-mono text-red-300">{g.paid.toLocaleString()}</div>
                <div className="col-span-2 text-right font-mono text-emerald-400">{g.hold.toLocaleString()}</div>
                <div className="col-span-1 text-right font-mono text-[color:var(--gold)]">{g.hold_pct.toFixed(1)}%</div>
              </li>
            ))}
            {data.gameplay.per_game.length === 0 && <li className="px-4 py-4 text-slate-500 text-sm">No gameplay yet.</li>}
          </ul>
        </div>
      </section>

      <section className="grid md:grid-cols-2 gap-4">
        <div className="vexis-card p-5" data-testid="fraud-panel">
          <div className="flex items-center justify-between">
            <div className="pill inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> Fraud alerts</div>
            <span className="text-xs text-slate-500 font-mono">{alerts.length} flagged</span>
          </div>
          <ul className="mt-3 divide-y divide-white/5 text-sm">
            {alerts.map((a) => {
              const tone = a.level === "high" ? "text-red-400 border-red-500/40" : a.level === "medium" ? "text-amber-300 border-amber-500/40" : "text-slate-300 border-white/10";
              return (
                <li key={a.user_id} className="py-2 flex items-start justify-between gap-3" data-testid={`fraud-${a.user_id}`}>
                  <div className="min-w-0">
                    <div className="text-slate-200 truncate">{a.user_email}</div>
                    <div className="text-xs text-slate-500 font-mono">dep ${a.deposits_usd.toFixed(2)} · out ${a.cashouts_usd.toFixed(2)}</div>
                    <ul className="mt-1 text-xs text-slate-400 list-disc list-inside">
                      {a.reasons.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                  <span className={`pill ${tone} shrink-0`}>{a.level} · {a.score}</span>
                </li>
              );
            })}
            {alerts.length === 0 && <li className="py-3 text-slate-500 text-sm">No risk signals detected.</li>}
          </ul>
        </div>

        <div className="vexis-card p-5">
          <div className="pill inline-flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5 text-emerald-400" /> Recent deposits</div>
          <ul className="mt-3 divide-y divide-white/5 text-sm">
            {data.recent_deposits.map((d, i) => (
              <li key={i} className="py-2 flex items-center justify-between gap-3" data-testid={`rev-dep-${i}`}>
                <div className="min-w-0">
                  <div className="font-mono text-emerald-300">+{usd(d.amount_usd)}</div>
                  <div className="text-xs text-slate-500 font-mono">{d.chips?.toLocaleString()} chips · {d.at ? new Date(d.at).toLocaleString() : "—"}</div>
                </div>
                <div className="text-xs text-slate-500 font-mono truncate max-w-[45%]">{d.reference}</div>
              </li>
            ))}
            {data.recent_deposits.length === 0 && <li className="py-3 text-slate-500 text-sm">No deposits yet.</li>}
          </ul>
        </div>
        <div className="vexis-card p-5">
          <div className="pill inline-flex items-center gap-1"><TrendingDown className="w-3.5 h-3.5 text-red-400" /> Recent cashouts</div>
          <ul className="mt-3 divide-y divide-white/5 text-sm">
            {data.recent_withdrawals.map((w, i) => (
              <li key={i} className="py-2 flex items-center justify-between gap-3" data-testid={`rev-wd-${i}`}>
                <div className="min-w-0">
                  <div className="font-mono text-red-300">−{usd(w.amount_usd)}</div>
                  <div className="text-xs text-slate-500 font-mono truncate">{w.user_email} · {w.method} · {w.at ? new Date(w.at).toLocaleString() : "—"}</div>
                </div>
                <div className="text-right min-w-0">
                  <div className="text-xs text-slate-500 font-mono truncate max-w-[180px]">{w.reference}</div>
                  <span className="pill mt-1 inline-flex">{w.status}</span>
                </div>
              </li>
            ))}
            {data.recent_withdrawals.length === 0 && <li className="py-3 text-slate-500 text-sm">No cashouts yet.</li>}
          </ul>
        </div>
      </section>
    </div>
  );
}
